<template>
    <div class="flex flex-col dark:text-white">
        <filters
            class="mt-3"
            :loading="loading"
            v-model="filters"
            category-filter
            :categories="categories"
            :types="types"
            date-filter
            status-filter
            id-filter
            :statuses="[
                { value: 'draft', label: 'Rascunho' },
                { value: 'pre-approved', label: 'Pré Aprovado' },
                { value: 'pending', label: 'Pendente' },
                { value: 'pending-correction', label: 'Correção Pendente' },
                { value: 'approved', label: 'Aprovado' },
                { value: 'reproved', label: 'Reprovado' },
            ]"
            :default-text="storedFilters.text"
            :default-id="storedFilters.id"
            :default-status="storedFilters.status"
            :default-types="storedFilters.types"
            :default-categories="storedFilters.categories"
            type-filter
            type-label="Tipo"
            search-filter
            search-label="Filtre pelo id ou nome"
            @fetch="
                products = [];
                next_page_url = false;
                this.pagination.current_page = 1;
                fetch();
            "
        >
            <form-toggle
                class="ml-3"
                v-model="commissions"
                :loading="loading"
                label="Vendas"
            >
            </form-toggle>
        </filters>
        <div class="my-3 flex items-center justify-between gap-3">
            <label
                class="flex items-center gap-2 text-sm text-gray-500 hover:cursor-pointer dark:text-gray-400"
            >
                <input
                    type="checkbox"
                    class="h-4 w-4 rounded border-black/20 text-indigo-500 focus:ring-indigo-500 dark:border-white/20"
                    :checked="all_selected"
                    @change="selectAll"
                />
                Selecionar tudo
                <span v-if="selected.length > 0" class="chip-indigo">
                    {{ selected.length }} selecionado{{
                        selected.length > 1 ? "s" : ""
                    }}
                </span>
            </label>
            <div class="flex items-center gap-3">
                <update-products
                    v-if="selected.length > 0"
                    :products="selected"
                    @fetch="
                        selected = [];
                        fetch();
                    "
                />
                <span class="text-xs tabular-nums text-gray-400">
                    Exibindo {{ pagination.from }} a {{ pagination.to }} de
                    {{ pagination.total }}
                </span>
            </div>
        </div>

        <ticto-loading v-if="loading" />
        <empty-state v-else-if="products.length === 0" />
        <div
            v-else
            class="not-prose relative overflow-hidden rounded-xl border border-black/5 bg-white shadow-xs dark:border-white/10 dark:bg-white/[0.02]"
        >
            <ul
                role="list"
                class="divide-y divide-black/5 dark:divide-white/[0.06]"
            >
                <li v-for="product in products" :key="product.id">
                    <div
                        class="flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-black/[0.02] dark:hover:bg-white/[0.03]"
                    >
                        <input
                            :id="'check-product-' + product.id"
                            type="checkbox"
                            class="h-4 w-4 shrink-0 rounded border-black/20 text-indigo-500 focus:ring-indigo-500 dark:border-white/20"
                            v-model="selected"
                            :value="product.id"
                        />

                        <a
                            @click.prevent="manage(product)"
                            class="min-w-0 flex-1 hover:cursor-pointer"
                        >
                            <div class="flex flex-wrap items-center gap-2">
                                <p
                                    class="truncate text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{ product.name }}
                                </p>
                                <product-status :status="product.status" />
                                <product-type :type="product.type" />
                                <span
                                    v-if="product.is_hidden"
                                    class="chip-slate"
                                >
                                    Oculto
                                </span>
                            </div>
                            <p
                                class="mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400"
                            >
                                #{{ product.id }} · {{ product.user.name }} ·
                                {{ product.category.name }}
                            </p>
                        </a>

                        <div
                            class="hidden shrink-0 flex-col items-end gap-0.5 md:flex"
                        >
                            <span
                                v-if="commissions"
                                class="text-sm font-medium tabular-nums text-gray-900 dark:text-white"
                            >
                                {{ $root.formatCurrency(product.commission ?? 0) }}
                            </span>
                            <time
                                :datetime="product.created_at"
                                class="text-xs tabular-nums text-gray-400 dark:text-gray-500"
                            >
                                {{ product.created_at }}
                            </time>
                        </div>

                        <template v-if="ebookSelected">
                            <div
                                v-if="loadingEbooks"
                                class="h-2 w-16 shrink-0 animate-pulse rounded bg-gray-300 dark:bg-gray-600"
                            ></div>
                            <button
                                v-else-if="productEbook(product)"
                                @click.stop="viewEbook(product)"
                                :disabled="loadingViewEbook"
                                class="chip-indigo shrink-0 hover:bg-indigo-500/20"
                            >
                                Download
                            </button>
                        </template>

                        <ChevronRightIcon
                            class="h-4 w-4 shrink-0 text-gray-300 dark:text-gray-600"
                            aria-hidden="true"
                        />
                    </div>
                </li>
            </ul>
        </div>

        <pagination
            v-if="pagination.total > pagination.per_page"
            class="mt-4"
            :pagination="pagination"
            @paginate="fetch"
        />
    </div>
</template>

<script>
import {
    MinusCircleIcon,
    CheckCircleIcon,
    ChevronRightIcon,
    CloudArrowDownIcon,
} from "@heroicons/vue/24/outline";
import { mapWritableState } from "pinia";
import FormToggle from "../../components/forms/FormToggle.vue";
import VueSelect from "../../components/forms/VueSelect.vue";
import Filters from "../../components/layouts/Filters.vue";
import Pagination from "../../components/layouts/Pagination.vue";
import ProductStatus from "../../components/layouts/ProductStatus.vue";
import ProductType from "../../components/layouts/ProductType.vue";
import SvgLoader from "../../components/layouts/SvgLoader.vue";
import EmptyState from "../../components/layouts/EmptyState.vue";
import TictoLoading from "../../components/layouts/TictoLoading.vue";
import UpdateProducts from "../../components/product/UpdateProducts.vue";
import { Store } from "../../stores/store.js";

export default {
    components: {
        FormToggle,
        UpdateProducts,
        Filters,
        EmptyState,
        TictoLoading,
        ChevronRightIcon,
        VueSelect,
        Pagination,
        CheckCircleIcon,
        MinusCircleIcon,
        ProductStatus,
        ProductType,
        CloudArrowDownIcon,
        SvgLoader,
    },
    data() {
        const stored = this.loadStoredFilters();
        return {
            loading: false,
            next_page_url: false,
            selected: [],
            all_selected: false,
            products: [],
            types: [],
            categories: [],
            commissions: false,
            pagination: {
                total: 0,
                per_page: 10,
                current_page: 1,
                last_page: 11,
                from: 1,
                to: 11,
            },
            filters: {
                "filter[text]": stored.text,
                "filter[id]": stored.id,
                "filter[status]": stored.status.join(","),
                "filter[type]": stored.types.join(","),
                "filter[category]": stored.categories.join(","),
            },
            storedFilters: stored,
            categoriesFilter: {
                type: Array,
                default: [],
            },
            ebooks: [],
            loadingEbooks: true,
            loadingViewEbook: false,
        };
    },

    computed: {
        ...mapWritableState(Store, ["storeProduct"]),

        filteredStatuses() {
            return this.filters.status.map((status) => {
                return {
                    value: status,
                    label: this.statuses.find((s) => s.value === status).label,
                };
            });
        },
        filteredTypes() {
            return this.filters.types.map((type) => {
                return {
                    value: type,
                    label: this.typesFilter.find((s) => s.value === type).label,
                };
            });
        },
        filteredCategories() {
            return this.filters.categories.map((category) => {
                return {
                    value: category,
                    label: this.categoriesFilter.find(
                        (s) => s.value === category,
                    ).label,
                };
            });
        },
        ebookSelected() {
            if (this.filters["filter[type]"]) {
                return this.filters["filter[type]"].includes("ebook");
            }

            return false;
        },
        headers() {
            let headers = [
                "",
                "Data",
                "Produto",
                "Produtor",
                "Status",
                "Vendas",
                "Tipo",
                "Categoria",
                "Visibilidade",
            ];
            if (this.ebookSelected) {
                headers.push("Anexo");
            }

            return headers;
        },
    },

    watch: {
        ebookSelected() {
            if (this.ebookSelected) {
                this.fetchEbooks();
            }
        },
        filters: {
            handler(value) {
                const split = (raw) =>
                    raw ? String(raw).split(",").filter(Boolean) : [];
                const persisted = {
                    text: value["filter[text]"] ?? "",
                    id: value["filter[id]"] ?? "",
                    status: split(value["filter[status]"]),
                    types: split(value["filter[type]"]),
                    categories: split(value["filter[category]"]),
                };
                localStorage.setItem(
                    "smaug_products_filters",
                    JSON.stringify(persisted),
                );
            },
            deep: true,
        },
    },

    mounted() {
        this.storeProduct = false;
        axios.get("/api/product/types").then(({ data }) => {
            this.types = Object.entries(data).map((entry) => {
                return { value: entry[0], label: entry[1] };
            });
        });
        axios.get("/api/product/categories").then(({ data }) => {
            this.categories = data.data;
            this.categoriesFilter = data.data;
        });
        this.fetch();
    },

    methods: {
        loadStoredFilters() {
            const defaults = {
                text: "",
                id: "",
                status: [],
                types: [],
                categories: [],
            };

            try {
                const stored = JSON.parse(
                    localStorage.getItem("smaug_products_filters"),
                );
                if (!stored) {
                    return defaults;
                }
                return {
                    ...defaults,
                    ...stored,
                    categories: (stored.categories ?? []).map((v) => {
                        const n = Number(v);
                        return Number.isFinite(n) ? n : v;
                    }),
                };
            } catch {
                return defaults;
            }
        },
        manage(product) {
            this.storeProduct = product;
            this.$router.push({
                name: "product",
                params: { product_id: product.id },
            });
        },
        selectAll() {
            this.all_selected = !this.all_selected;
            if (this.all_selected) {
                axios
                    .get("/api/product/select-all", {
                        params: this.filters,
                    })
                    .then(({ data }) => {
                        this.selected = data;
                    })
                    .catch((error) => {
                        this.loading = false;
                        this.$toast.error(error.response.data.message);
                    });
            } else {
                this.selected = [];
            }
        },
        fetch() {
            this.loading = true;

            const params = this.filters;
            params["page"] = this.pagination.current_page;
            if (this.commissions) {
                params.commissions = true;
            }
            axios
                .get("/api/product", {
                    params: params,
                })
                .then(({ data }) => {
                    this.products = data.data;

                    this.pagination = {
                        ...this.pagination,
                        ...data,
                    };

                    this.next_page_url = data.next_page_url;

                    if (this.ebookSelected) {
                        this.fetchEbooks();
                    }

                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    // this.$toast.error(error.response.data.message);
                });
        },
        fetchEbooks() {
            this.loadingEbooks = true;

            const params = this.filters;
            params["page"] = this.pagination.current_page;

            axios
                .get("/api/product/ebooks", {
                    params: params,
                })
                .then(({ data }) => {
                    this.ebooks = data;
                    this.loadingEbooks = false;
                })
                .catch((error) => {
                    this.loadingEbooks = false;
                    // this.$toast.error(error.response.data.message);
                });
        },
        productEbook(product) {
            return this.ebooks.find((ebook) => ebook.product_id === product.id);
        },
        viewEbook(product) {
            this.loadingViewEbook = true;
            axios
                .get("/api/product/" + product.id + "/ebook/show")
                .then(({ data }) => {
                    this.loadingViewEbook = false;
                    window.open(data.url, "_blank");
                });
        },
    },
};
</script>
